import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ImageUploadSection = ({ images, onImagesUpdate }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = async (files) => {
    setUploading(true);
    const newImages = [];
    
    for (let i = 0; i < files?.length; i++) {
      const file = files?.[i];
      if (file?.type?.startsWith('image/')) {
        // Simulate upload delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const mockImageUrl = `https://images.unsplash.com/photo-${Date.now() + i}?w=400&h=300&fit=crop`;
        newImages?.push({
          id: Date.now() + i,
          url: mockImageUrl,
          name: file?.name,
          size: file?.size,
          type: file?.type,
          uploadedAt: new Date()?.toISOString()
        });
      }
    }
    
    onImagesUpdate([...images, ...newImages]);
    setUploading(false);
  };

  const removeImage = (imageId) => {
    onImagesUpdate(images?.filter(img => img?.id !== imageId));
  };

  const setAsLogo = (imageId) => {
    const updatedImages = images?.map(img => ({
      ...img,
      isLogo: img?.id === imageId
    }));
    onImagesUpdate(updatedImages);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    let i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
          <Icon name="Image" size={20} />
          <span>Store Images</span>
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef?.current?.click()}
          iconName="Upload"
          iconPosition="left"
          disabled={uploading}
        >
          Upload Images
        </Button>
      </div>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-micro ${
          dragActive 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Icon name="Upload" size={24} className="text-muted-foreground" />
          </div>
          <div>
            <p className="text-foreground font-medium">
              {uploading ? 'Uploading images...' : 'Drop images here or click to upload'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Supports JPG, PNG, GIF up to 10MB each
            </p>
          </div>
        </div>

        {uploading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-sm text-foreground">Uploading...</span>
            </div>
          </div>
        )}
      </div>
      {/* Image Gallery */}
      {images?.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-foreground">Uploaded Images</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images?.map((image) => (
              <div key={image?.id} className="relative group bg-card border border-border rounded-lg overflow-hidden">
                <div className="aspect-video relative">
                  <Image
                    src={image?.url}
                    alt={image?.name}
                    className="w-full h-full object-cover"
                  />
                  {image?.isLogo && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-md">
                      Logo
                    </div>
                  )}
                  
                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-micro flex items-center justify-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setAsLogo(image?.id)}
                      iconName="Star"
                      disabled={image?.isLogo}
                    >
                      {image?.isLogo ? 'Logo' : 'Set as Logo'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => removeImage(image?.id)}
                      iconName="Trash2"
                    />
                  </div>
                </div>
                
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground truncate">{image?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(image?.size)} • {new Date(image.uploadedAt)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadSection;